import React, { useState } from 'react';
import { Rapport, UpdateRapportRequest } from '../../services/reportService';
import ToggleSwitch from '../Common/ToggleSwitch';
import './ReportEditForm.css';

interface ReportEditFormProps {
  report: Rapport;
  onSubmit: (data: UpdateRapportRequest) => void;
  onCancel: () => void;
  onReplaceFile: (file: File) => void;
  loading?: boolean;
}

const ReportEditForm: React.FC<ReportEditFormProps> = ({ 
  report, 
  onSubmit, 
  onCancel, 
  onReplaceFile, 
  loading = false 
}) => {
  const [formData, setFormData] = useState<UpdateRapportRequest>({
    nomRapport: report.nomRapport,
    dossier: report.dossier,
    description: report.description || '',
    isDisabled: report.isDisabled,
    isPreview: report.isPreview
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | null>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nomRapport.trim()) {
      newErrors.nomRapport = 'Le nom du rapport est obligatoire';
    }

    if (!formData.dossier.trim()) {
      newErrors.dossier = 'Le dossier est obligatoire';
    }

    if (file && !file.name.endsWith('.trdp')) {
      newErrors.file = 'Le fichier doit être un rapport Telerik (.trdp)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    
    // Effacer l'erreur du fichier si un fichier valide est sélectionné
    if (selectedFile && selectedFile.name.endsWith('.trdp')) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleReplaceFile = () => {
    if (file) {
      onReplaceFile(file);
    }
  };

  return (
    <div className="report-edit-form">
      <h3>Modifier le Rapport</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="nomRapport">
            Nom du rapport <span className="required">*</span>
          </label>
          <input
            type="text"
            id="nomRapport"
            value={formData.nomRapport}
            onChange={(e) => setFormData(prev => ({ ...prev, nomRapport: e.target.value }))}
            className={errors.nomRapport ? 'error' : ''}
            placeholder="Entrez le nom du rapport"
            disabled={loading}
          />
          {errors.nomRapport && <span className="error-message">{errors.nomRapport}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="dossier">
            Dossier <span className="required">*</span>
          </label>
          <input
            type="text"
            id="dossier"
            value={formData.dossier}
            onChange={(e) => setFormData(prev => ({ ...prev, dossier: e.target.value }))}
            className={errors.dossier ? 'error' : ''}
            placeholder="Entrez le nom du sous-dossier"
            disabled={loading}
          />
          {errors.dossier && <span className="error-message">{errors.dossier}</span>}
          <small className="help-text">
            Le fichier sera stocké dans : /Reports/{formData.dossier || '[dossier]'}/
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Description du rapport (optionnel)"
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>Statut</label>
          <div className="toggle-container">
            <ToggleSwitch
              checked={!formData.isDisabled}
              onChange={(checked) => setFormData(prev => ({ ...prev, isDisabled: !checked }))}
              size="medium"
              labels={{ on: 'Activé', off: 'Désactivé' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Mode d'affichage</label>
          <div className="toggle-container">
            <ToggleSwitch
              checked={formData.isPreview}
              onChange={(checked) => setFormData(prev => ({ ...prev, isPreview: checked }))}
              size="medium"
              labels={{ on: 'Aperçu', off: 'Interactif' }}
            />
          </div>
          <small className="help-text">
            Mode Aperçu : Affichage optimisé pour l'impression avec pagination<br/>
            Mode Interactif : Affichage continu pour la consultation à l'écran
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="file">
            Remplacer le fichier .trdp
          </label>
          <div className="file-input-container">
            <input
              type="file"
              id="file"
              accept=".trdp"
              onChange={handleFileChange}
              className={errors.file ? 'error' : ''}
              disabled={loading}
            />
            <label htmlFor="file" className="file-input-label">
              {file ? file.name : 'Sélectionner un nouveau fichier .trdp'}
            </label>
          </div>
          {errors.file && <span className="error-message">{errors.file}</span>}
          <small className="help-text">
            Fichier actuel : {report.fichiers.split('\\').pop() || report.fichiers}
          </small>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Mise à jour...' : 'Mettre à jour'}
          </button>
          
          {file && (
            <button 
              type="button" 
              className="btn btn-warning"
              onClick={handleReplaceFile}
              disabled={loading}
            >
              Remplacer le fichier
            </button>
          )}
          
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportEditForm;
